import { DatePicker, Form, Input, InputNumber, message } from "antd";
import { observer } from "mobx-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import CustomButton from "base/ui/components/Button";
import BoxContainer from "base/ui/components/BoxContainer";
import { ActionType, ProColumns } from "@ant-design/pro-components";
import PageWrapper from "pages/Components/PageWrapper";
import CustomProTable, {
  tablePagination,
} from "base/ui/components/CustomTable";
import { SearchBox, SearchForm, SearchItem } from "pages/Components/SearchBox";
import CustomSelect from "base/ui/components/CustomSelect";
import {
  commonPresenter,
  commonStore,
  surveyPresenter,
  surveyStore,
} from "stores/root_store";
import { cloneDeep } from "lodash";
import { Answer, TableReportHeader } from "services/Survey/survey_services";
import { MVC_URL, PAGE_SIZE_DEFAULT, VARIABLE_CHECK_ZALO } from "utils/constants";
import { SearchModel } from "pages/model";
import { Download, Filter } from "base/ui/components/Icons";
import { useTranslation } from "react-i18next";
import { CommonModel } from "services/Main/common_services";
import dayjs from "dayjs";
import { debounce } from "utils/debounce";
import { checkPermission, useCheckPermission } from "utils/permissionHelper";
import { Trans } from 'react-i18next';
import usePartialState from 'hooks/usePartialState';
import useDebounce from 'hooks/useDebounce';

const { RangePicker } = DatePicker;

const initialColumn: ProColumns<Record<string, any>>[] = [
  {
    title: <Trans i18nKey={'zalo-notification.stt'} />,
    dataIndex: "STT",
    key: "STT",
    align: "center",
    width: 50,
  },
  // Mã khảo sát
  {
    title: <Trans i18nKey={'survey.profile-code'} />,
    dataIndex: "MKS",
    key: "MKS",
    width: 100,
  },
  // Tên khảo sát
  {
    title: <Trans i18nKey={'survey.profile-name'} />,
    dataIndex: "AKS",
    key: "AKS",
    width: 200,
  },
  // tracking id
  {
    title: 'Tracking ID',
    dataIndex: "TASKCODE",
    key: "TASKCODE",
    width: 100,
    render: (_, record) => {
      return (
        <a target='_blank' href={MVC_URL + `/Work/Task/Edit/${record.TRACKINGID}`} rel="noreferrer">{_}</a>
      )
    }
  },
  // Tên đối tác
  {
    title: 'Tên đối tác',
    dataIndex: "PRNAME",
    key: "PRNAME",
    width: 200,
  },
  // Mã đối tác
  {
    title: 'Mã đối tác',
    dataIndex: "PRCODE",
    key: "PRCODE",
    width: 200,
  },
  // Nhân viên thực hiện
  {
    title: 'Nhân viên thực hiện nhiệm vụ',
    dataIndex: "NVTH",
    key: "NVTH",
    width: 200,
  },
  // Thời gian gửi đánh giá (thời gian phản hồi)
  {
    title: 'Thời gian phản hồi',
    dataIndex: "TGGDG",
    key: "TGGDG",
    width: 170,
    onCell: (_, record) => ({
      className: "text-center",
    }),
    valueType: "dateTime",
    fieldProps: {
      format: "DD/MM/YYYY HH:mm:ss",
    },
  },
  // Tổng điểm
  {
    title: <Trans i18nKey={'survey.total-point'} />,
    dataIndex: "TĐ",
    key: "TĐ",
    width: 100,
    onCell: (_, record) => ({
      className: "text-right",
    }),
  },
  // Phần trăm
  {
    title: <Trans i18nKey={'survey.percent'} />,
    dataIndex: "PT",
    key: "PT",
    width: 100,
    onCell: (_, record) => ({
      className: "text-right",
    }),
  },
];

export function createSurveyReport() {
  return observer(() => {
    useCheckPermission("INDEX");
    // ============ Navigation =================/
    const { t } = useTranslation();

    const [form] = Form.useForm();
    const tableRef = useRef<ActionType>();

    // Tìm kiếm / phân trang
    const [dataPaging, setDataPaging] = useState<SearchModel>({
      paging: {
        pageSize: PAGE_SIZE_DEFAULT,
        pageIndex: 1,
        orderBy: undefined,
        orderByDesc: undefined,
      },
      fromTime: undefined,
      toTime: undefined,
      surveyId: undefined,
      env: VARIABLE_CHECK_ZALO,
    });
    // data table mẫu khảo sát
    const [dataTable, setDataTable] = useState<Record<string, any>[]>([]);
    // data common date
    const [listCommonDate, setListCommonDate] = useState<CommonModel[]>([]);
    // disable rangePicker khi chọn ngày từ common
    const [disablePicker, setDisablePicker] = usePartialState({
      date1: false,
      date2: false
    });
    const [commonSurvey, setCommonSurvey] = useState<CommonModel[]>([]);
    // loading khi xuất Excel
    const [exportLoading, setExportLoading] = useState(false);

    const [columns, setColumns] = useState<ProColumns<Record<string, any>>[]>(
      initialColumn
    );

    const [listCreateBy, setListCreateBy] = useState<CommonModel[]>([]);

    // CÁC HÀM LẤY DATA
    const getDataTable = async (dataSearch: SearchModel) => {
      // kiểm tra phải có surveyId để call api lấy data
      if (dataSearch.surveyId) {
        await surveyPresenter.getDataSurveyReport(surveyStore, dataSearch);
        // check thành công để gắn data
        if (surveyStore.dataSurveyReport.isSuccess) {
          /*
            gắn data vào trong từng field con trong children trong từng cột con(điểm, trả lời)
          */

          const dataTemp = cloneDeep(surveyStore.dataSurveyReport.data);
          console.log('dataTemp', dataTemp);
          const totalRow = dataTemp.at(0)?.value.length || 0;
          const arrData = [] as any[];
          for (let index = 0; index < totalRow; index++) {
            const dataReturn = {};
            // lọc qua từng dòng trong danh sách data để thêm những data vào các cột khảo sát mới (điểm và câu trả lời)
            dataTemp.forEach((d) => {
              if (typeof d.value[index] === "object") {
                const t = d.value[index] as Answer;
                Object.assign(dataReturn, {
                  [`${d.key}_Answer`]: t?.answer,
                  [`${d.key}_Score`]: t?.score,
                });
              } else {
                Object.assign(dataReturn, { [`${d.key}`]: d.value[index] });
              }
            });
            // gán lại data sau khi map vào lại state để render lại table
            Object.assign(dataReturn, { STT: index + 1 });
            arrData.push(dataReturn);
          }
          setDataTable(arrData);

          return arrData;
        }
      }
      setDataTable([]);
      return [];
    };


    /*
      HÀM LẤY CỘT TỪ API
      đã set trước các cột mặc định, gắn thêm 4 cột
      có phần children (điểm, câu trả lời) vào vị
      trí index thứ 5
    */
    const getTableColumns = async (surveyId: string) => {
      const result = await surveyPresenter.getTabaleReportHeader(surveyId);
      if (result.isSuccess) {
        const dataColumn: ProColumns<Record<string, any>>[] = result.data.map(
          (column: TableReportHeader) => ({
            title: column.display,
            key: column.key,
            dataIndex: column.key,
            align: "left",
            children: column.childrens.length
              ? column.childrens.map((child) => ({
                title: child.keyChildren === "Score" ? "Điểm" : "Câu trả lời",
                key: column.key + "_" + child.keyChildren,
                dataIndex: column.key + "_" + child.keyChildren,
                width: child.keyChildren === "Score" ? 100 : 250,
                onCell: (_, record) => ({
                  className:
                    child.dataType === "number" ? "text-right" : "text-left",
                }),
              }))
              : undefined,
          })
        );

        const columnTemp = cloneDeep(initialColumn);
        columnTemp.splice(6, 0, ...dataColumn);
        setColumns(columnTemp);
      }
    };

    // LẤY DANH SÁCH COMMON DATE -----------------------------------------------------------------------
    const getListCommonDate = async () => {
      await commonPresenter.getListSelectCommonDate(commonStore);
      if (commonStore.commonRespone.isSuccess) {
        setListCommonDate(commonStore.commonRespone.data);
      }
    };


    // Lấy danh sách người tạo
    const getListCreateBy = async (key = '') => {
      await commonPresenter.getListPersonInCharge(commonStore, key);
      if (commonStore.commonRespone.isSuccess) {
        setListCreateBy(commonStore.commonRespone.data);
      }
    };
    const searchCreateBy = useDebounce(getListCreateBy, 500);

    // LẤY NGÀY BẰNG COMMON DATA -----------------------------------------------------------------------
    /*
      hàm set tùy chỉnh ngày tháng, nếu chọn tùy chỉnh thì cho chọn ngày
      còn không thì sẽ lấy data từ api để set vào from date to date
    */
    const getDateByKey = async (key: string) => {
      await commonPresenter.getDateByCommonDate(commonStore, key);
      if (commonStore.commonDate.fromDate) {
        form.setFieldValue("ngayDanhGia", [
          dayjs(commonStore.commonDate.fromDate),
          dayjs(commonStore.commonDate.toDate),
        ]);
      } else {
        form.setFieldValue("ngayDanhGia", undefined);
      }
    };

    const getDateByKeyCreateDate = async (key: string) => {
      await commonPresenter.getDateByCommonDate(commonStore, key);
      if (commonStore.commonDate.fromDate) {
        form.setFieldValue("thoiGianTao", [
          dayjs(commonStore.commonDate.fromDate),
          dayjs(commonStore.commonDate.toDate),
        ]);

        getListCommonSurvey(undefined, commonStore.commonDate.fromDate, commonStore.commonDate.toDate)
      } else {
        form.setFieldValue("thoiGianTao", undefined);
      }
    };

    // DANH SÁCH COMMON MẪU KHẢO SÁT -------------------------------------------------------------------------
    const getListCommonSurvey = async (key?: string, createTimeFrom?: string, createTimeTo?: string) => {
      const listCommon = await surveyPresenter.getListCommonSurvey(key, createTimeFrom, createTimeTo);
      setCommonSurvey(listCommon);
    };

    // hạn chế việc call api nhiều lần -----------------------------------------------------------------------
    const debounceCommonSurvey = useCallback(
      debounce(getListCommonSurvey, 300),
      []
    );

    useEffect(() => {
      getListCommonDate();
      getListCommonSurvey();

      // nhân viên thực hiện
      searchCreateBy()
    }, []);

    // ===============================SEARCH BOX ==============================================================
    const [open, setOpen] = useState(false);
    const showDrawer = () => {
      setOpen(true);
    };

    const onClose = () => {
      setOpen(false);
    };


    return (
      <PageWrapper
        breadcrumb={[
          {
            text: "Báo cáo kết quả khảo sát, đánh giá",
          },
        ]}
        extras={
          <>
            {checkPermission("EXPORT") && (
              <CustomButton
                type="primary"
                loading={exportLoading}
                icon={<Download className={"!fill-white"} />}
                onClick={async () => {
                  if (dataPaging.surveyId) {
                    const dataSearch = cloneDeep(dataPaging);
                    dataSearch.paging = undefined;
                    setExportLoading(true);
                    await surveyPresenter.exportExcelSurvey(dataSearch).finally(() => {
                      setExportLoading(false);
                    });
                  } else {
                    message.warning(
                      'Vui lòng chọn "Mẫu khảo sát" để xuất báo cáo!'
                    );
                  }
                }}
              >
                {t("btn.export")}
              </CustomButton>
            )}
            <CustomButton
              onClick={showDrawer}
              type="primary"
              color="#EFF4F9"
              className="!px-0 w-8"
            >
              <Filter className="!fill-primary" />
            </CustomButton>
          </>
        }
      >
        <BoxContainer>
          <CustomProTable<Record<string, any>>
            options={false}
            handleResize={() => { }}
            bordered
            dataSource={dataTable}
            columns={columns}
            actionRef={tableRef}
            request={async (params = {}, sort, filter) => {
              const data = await getDataTable({
                ...dataPaging,
                paging: {
                  ...dataPaging.paging,
                  orderBy:
                    Object.values(sort)[0] === "ascend"
                      ? `${Object.keys(sort)[0].split(",").pop()}`
                      : undefined,
                  orderByDesc:
                    Object.values(sort)[0] === "descend"
                      ? `${Object.keys(sort)[0].split(",").pop()}`
                      : undefined,
                },
              });

              if (Object.values(sort).length) {
                setDataPaging({
                  ...dataPaging,
                  paging: {
                    ...dataPaging.paging,
                    orderBy:
                      Object.values(sort)[0] === "ascend"
                        ? `${Object.keys(sort)[0].split(",").pop()}`
                        : undefined,
                    orderByDesc:
                      Object.values(sort)[0] === "descend"
                        ? `${Object.keys(sort)[0].split(",").pop()}`
                        : undefined,
                  },
                });
              }
              return {
                data: data,
                success: surveyStore.dataSurveyReport.isSuccess,
              };
            }}
            rowKey="STT"
            pagination={{
              ...tablePagination(dataPaging, setDataPaging),
              total: surveyStore.dataSurveyReport.paging?.totalCount,
            }}
          />
        </BoxContainer>

        <SearchBox
          onClose={onClose}
          open={open}
          className="lg:!w-[50%]"
          footer={
            <CustomButton
              htmlType="submit"
              className="my-auto ml-auto"
              type="primary"
              onClick={() => {
                form.submit();
              }}
            >
              {t("btn.search")}
            </CustomButton>
          }
        >
          <SearchForm
            form={form}
            onFinish={(value) => {
              const ngayDanhGia = value["ngayDanhGia"];
              const thoiGianTao = value["thoiGianTao"];

              let fromTime = undefined;
              let toTime = undefined;
              let createTimeFrom = undefined;
              let createTimeTo = undefined;
              if (ngayDanhGia) {
                fromTime = ngayDanhGia[0].format("YYYY-MM-DD") || undefined;
                toTime = ngayDanhGia[1].format("YYYY-MM-DD") || undefined;
              }
              if (thoiGianTao) {
                createTimeFrom = thoiGianTao[0].format("YYYY-MM-DD") || undefined;
                createTimeTo = thoiGianTao[1].format("YYYY-MM-DD") || undefined;
              }
              setDataPaging({
                ...value,
                paging: { ...dataPaging.paging, pageIndex: 1 },
                fromTime: fromTime,
                toTime: toTime,
                createTimeFrom: createTimeFrom,
                createTimeTo: createTimeTo,
                env: VARIABLE_CHECK_ZALO
              });
              getTableColumns(value["surveyId"]);
              tableRef.current?.reload();
            }}
          >
            {/* Thời gian tạo  */}
            <SearchItem name={"commonDate2"} label={"Thời gian tạo"} start>
              <CustomSelect
                options={listCommonDate}
                fieldNames={{ label: "value", value: "key" }}
                onChange={async (value, option) => {
                  if (option) {
                    setDisablePicker({ date2: option.key === "Custom" ? false : true });
                    await getDateByKeyCreateDate(option.key);
                  } else {
                    getListCommonSurvey(undefined)
                    setDisablePicker({ date2: false });
                    form.setFieldValue("thoiGianTao", undefined);
                  }
                }}
                allowClear
              />
            </SearchItem>

            {/* chọn ngày*/}
            <SearchItem name={"thoiGianTao"} col2>
              <RangePicker
                onChange={async (values, formatString) => {
                  const thoiGianTao = form.getFieldValue('thoiGianTao');
                  let createTimeFrom = undefined;
                  let createTimeTo = undefined;
                  if (thoiGianTao) {
                    createTimeFrom = thoiGianTao[0].format("YYYY-MM-DD") || undefined;
                    createTimeTo = thoiGianTao[1].format("YYYY-MM-DD") || undefined;
                  }
                  await getListCommonSurvey(undefined, createTimeFrom, createTimeTo)

                }}
                disabled={disablePicker.date2}
                format="DD/MM/YYYY"
                className="w-full"
              />
            </SearchItem>


            {/* Tên khảo sát  */}
            <SearchItem
              rules={[{ required: true, message: 'Vui lòng nhập thông tin' }]}
              label={"Tên khảo sát"}
              name={"surveyId"}
              col2
            >
              <CustomSelect
                options={commonSurvey}
                fieldNames={{ label: "value", value: "key" }}
                filterOption={false}
                onSearch={(value) => debounceCommonSurvey(value || undefined)}
                onClear={() => debounceCommonSurvey()}
                onClick={() => debounceCommonSurvey()}
                showSearch
                allowClear
              />
            </SearchItem>


            {/* Tên khách hàng */}
            <SearchItem name={"profileName"} label="Tên khách hàng">
              <Input />
            </SearchItem>
            {/* Mã khách hàng */}
            <SearchItem name={"profileCode"} label="Mã khách hàng">
              <Input />
            </SearchItem>

            {/* trạng thái  */}
            {/* <SearchItem label={"Trạng thái"} name={"trangthaiguitin"}>
              <CustomSelect options={dropdownstatus} />
            </SearchItem> */}

            {/* Ngày đánh giá  */}
            <SearchItem name={"commonDate"} label={"Ngày đánh giá"} start>
              <CustomSelect
                options={listCommonDate}
                fieldNames={{ label: "value", value: "key" }}
                onChange={async (value, option) => {
                  if (option) {
                    setDisablePicker({ date1: option.key === "Custom" ? false : true });
                    await getDateByKey(option.key);
                  } else {
                    setDisablePicker({ date1: false });
                    form.setFieldValue("ngayDanhGia", undefined);
                  }
                }}
                allowClear
              />
            </SearchItem>

            {/* chọn ngày*/}
            <SearchItem name={"ngayDanhGia"} col2>
              <RangePicker
                disabled={disablePicker.date1}
                format="DD/MM/YYYY"
                className="w-full"
              />
            </SearchItem>


            {/* NHÂN VIÊN THỰC HIỆN NHIỆM VỤ */}
            <SearchItem name={'empPerform'} label={'Nhân viên thực hiện nhiệm vụ'}>
              <CustomSelect
                options={listCreateBy}
                fieldNames={{ label: 'value', value: 'key' }}
                filterOption={false}
                onSearch={(value) => {
                  searchCreateBy(value);
                }}
                onClear={() => searchCreateBy()}
                onClick={() => searchCreateBy()}
                allowClear
                showSearch
              />
            </SearchItem>


            {/* Điểm khảo sát từ */}
            <SearchItem start label={`Điểm khảo sát từ`} name={"fromScore"}>
              <InputNumber className="!w-full " />
            </SearchItem>
            {/* Điểm khảo sát đến */}
            <SearchItem label={`Điểm khảo sát đến`} name={"toScore"}>
              <InputNumber className="!w-full" />
            </SearchItem>
          </SearchForm>
        </SearchBox>
      </PageWrapper>
    );
  });
}
